import type { Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  type ProcessDefinitionPutOptionV1Dto,
  type ProcessDefinitionPutStepV1Dto,
  type ProcessDefinitionPutTriggerParamV1Dto,
  type ProcessDefinitionPutV1Dto,
  ProcessDefinitionsService,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { Process, ProcessResponse, Step, StepID, triggerPlugin } from '../processTypes';
import { formatToID } from '../utils';

export function useProcesses() {
  const router = useRouter();
  const route = useRoute();

  /**
   * Recursively remove all null/undefined values.
   * Also removes returnToParentProcess when it is false, so the flag is only persisted for true.
   */
  function removeNullValues<T extends Record<string, any> | any[]>(obj: T): T {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => removeNullValues(item)) as T;
    }

    if (typeof obj === 'object') {
      const cleaned: Record<string, any> = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          !(key === 'returnToParentProcess' && value === false)
        ) {
          cleaned[key] = removeNullValues(value);
        }
      });
      return cleaned as T;
    }

    return obj;
  }

  const mapProcessDefinitionStepOptionToPutPayload = (
    option: Step['options'][number],
  ): ProcessDefinitionPutOptionV1Dto => ({
    name: option.name,
    nextStep: option.nextStep ?? '',
    team: option.team ?? '',
    isPublic: option.isPublic ?? false,
  });

  const mapProcessDefinitionStepTriggerToPutPayload = (trigger: triggerPlugin) => ({
    id: trigger.id,
    params: trigger.params?.map(
      (param: ProcessDefinitionPutTriggerParamV1Dto) =>
        ({
          name: param.name,
          value: param.value,
        }) satisfies ProcessDefinitionPutTriggerParamV1Dto,
    ),
  });

  const mapProcessDefinitionStepToPutPayload = (step: Step): ProcessDefinitionPutStepV1Dto => ({
    id: step.id,
    name: step.name,
    description: step.description,
    options: step.options?.map(mapProcessDefinitionStepOptionToPutPayload) ?? [],
    triggers: step.triggers?.map(mapProcessDefinitionStepTriggerToPutPayload) ?? [],
    uiPlugins: step.uiPlugins,
  });

  const mapProcessDefinitionDataToPutPayload = (
    data: ProcessResponse,
  ): ProcessDefinitionPutV1Dto => ({
    description: data.description,
    id: data.id,
    isDraft: data.isDraft ?? true,
    name: data.name,
    start: data.start.toString(),
    steps: data.steps?.map(mapProcessDefinitionStepToPutPayload) ?? [],
    version: data.version,
  });

  function cleanProcessBeforeSave(process: Process): Process {
    const cleanedSteps = process.steps
      .map((step) => {
        const options = step.options
          ?.filter(
            (opt) =>
              (typeof opt?.nextStep === 'string' && opt.nextStep.trim() !== '') ||
              opt?.returnToParentProcess === true,
          )
          .map((opt) => {
            // Remove returnToStepId and returnOptionName for return-to-parent options
            if (opt.returnToParentProcess === true) {
              return {
                ...opt,
                returnToStepId: null,
                returnOptionName: null,
              };
            }
            return opt;
          });

        return {
          ...step,
          options,
        };
      })
      .filter((step) => step.id?.trim() !== '');

    // Remove all null values recursively before returning
    return removeNullValues({
      ...process,
      steps: cleanedSteps,
    });
  }

  const fetchProcesses = async (): Promise<Process[]> => {
    try {
      const loaded = await ProcessDefinitionsService.processDefinitionControllerV1ListV1(false);
      // Clean invalid options from all loaded processes
      return loaded.map((process: Process) => cleanProcessBeforeSave(process));
    } catch (_err) {
      throw new Error('Failed to fetch processes');
    }
  };

  const fetchProcess = async (id: string): Promise<Process> => {
    try {
      const loaded = await ProcessDefinitionsService.processDefinitionControllerV1GetV1(id);
      // Clean invalid options from loaded process (nextStep=null && returnToParentProcess=false)
      return cleanProcessBeforeSave(loaded);
    } catch (_err) {
      throw new Error('Failed to fetch processes');
    }
  };

  const createProcess = async (data: ProcessResponse) => {
    try {
      await ProcessDefinitionsService.processDefinitionControllerV1PutV1(
        data.id as string,
        mapProcessDefinitionDataToPutPayload(data),
      );
      eventBus.emit('update');
    } catch (_err) {
      throw new Error('Error creating process');
    }
  };

  const updateProcess = async (data: ProcessResponse) => {
    try {
      await ProcessDefinitionsService.processDefinitionControllerV1PutV1(
        data.id as string,
        mapProcessDefinitionDataToPutPayload(data),
      );
      eventBus.emit('update');
    } catch (_err) {
      throw new Error('Error updating process');
    }
  };

  async function createNewProcess(process: Process) {
    await updateProcess(cleanProcessBeforeSave(process));

    if (route.path === '/processes/new' && process?.id?.trim()) {
      void router.replace(`/processes/${process.id}`);
    }
  }

  const deleteProcess = async ({ id, version }: { id: string; version: number }) => {
    try {
      await ProcessDefinitionsService.processDefinitionControllerV1DeleteV1(id, version);
      eventBus.emit('delete');
    } catch (_err) {
      throw new Error('Error deleting process');
    }
  };

  const deleteProcessInBatch = async ({ id, version }: { id: string; version: number }) => {
    try {
      await ProcessDefinitionsService.processDefinitionControllerV1DeleteV1(id, version);
    } catch (_err) {
      throw new Error('Error deleting processes in batch');
    }
  };

  const deleteProcesses = async (processesToDelete: { id: string; version: number }[]) => {
    try {
      await Promise.all(processesToDelete.map((process) => deleteProcessInBatch(process)));
      eventBus.emit('delete');
    } catch (_errorrror) {
      throw new Error('Fehler beim Batch-Löschen der Prozesse');
    }
  };

  async function handleProcessInitSubmit({
    name,
    description,
    json,
    processRef,
    closeModal,
  }: {
    name: string;
    description: string;
    json?: any;
    processRef: Ref<Process>;
    closeModal?: () => void;
  }) {
    const process = processRef.value;
    const imported = json?.json ?? json;

    process.id = formatToID(name);
    process.name = name;
    process.description = description;

    if (imported) {
      process.start = imported.start ?? imported.steps?.[0]?.id;
      process.steps = imported.steps || [];
    }

    await createProcess(cleanProcessBeforeSave(process));

    if (closeModal) closeModal();

    if (route.path === '/processes/new' && process?.id?.trim()) {
      void router.replace(`/processes/${process.id}`);
    }
  }

  function extractProcessNameFromProcdefUrl(procdefUrl: string): string | undefined {
    try {
      const urlObj = new URL(procdefUrl);
      return urlObj.hostname;
    } catch (_error) {
      // No valid URL-String – fallback with RegEx
      const match = procdefUrl.match(/\/\/?([^\/]+)/);
      if (match) {
        return match[1];
      } else {
        // Cannot extract process name from string
        return undefined;
      }
    }
  }

  function extractProcessStepIdFromProcdefUrl(procdefUrl: string): string | undefined {
    try {
      const urlObj = new URL(procdefUrl);
      const pathSegments = urlObj.pathname.split('/').filter((seg) => seg.length > 0);
      if (pathSegments.length > 0) {
        return pathSegments[pathSegments.length - 1];
      }
      return undefined;
    } catch (_error) {
      // No valid URL-String – fallback with RegEx
      const parts = procdefUrl.split('/');
      if (parts.length > 1) {
        return parts[parts.length - 1];
      }
      // Cannot extract stepID from string
      return undefined;
    }
  }

  function extractProcessStartStepIdFromProcessID({
    processes,
    processID,
  }: {
    processes: Process[];
    processID: string;
  }): StepID | undefined {
    const process = processes.find((process) => process.id === processID);
    return process?.start;
  }

  function handleProcessInitModalClose({
    newVisibility,
    process,
    setShowModal,
  }: {
    newVisibility: boolean;
    process: Process;
    setShowModal: (val: boolean) => void;
  }) {
    setShowModal(newVisibility);

    if (!newVisibility && !process?.name?.trim()) {
      void router.push('/processes');
    }
  }

  function isUrl(value: string): boolean {
    try {
      new URL(value);
      return true;
    } catch (_) {
      return false;
    }
  }

  const downloadProcess = (process: Process) => {
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

    const fileName = `${timestamp}_${process.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}.json`;

    const blob = new Blob([exportProcessAsOrderedJson(process)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const exportProcessAsJson = (process: Process) => {
    return JSON.stringify(removeNullValues(process), null, 2);
  };

  // Helper to build clean option for export (excludes return-to-step fields for return-to-parent options)
  function buildOptionForExport(option: any) {
    const baseOption = {
      name: option.name,
      nextStep: option.nextStep,
      team: option.team,
      returnToParentProcess: option.returnToParentProcess === true ? true : undefined,
    };
    // Only include return-to-step fields for external (non-return-to-parent) options
    if (option.returnToParentProcess === true) {
      return baseOption;
    }
    // Remove null values from the final option object
    return removeNullValues({
      ...baseOption,
      returnToStepId: option.returnToStepId ?? null,
      returnOptionName: option.returnOptionName ?? null,
    });
  }

  const exportProcessAsOrderedJson = (process: Process) => {
    const orderedProcess = {
      id: process.id,
      name: process.name,
      description: process.description,
      version: process.version,
      isDraft: process.isDraft,
      start: process.start,
      steps: process.steps.map((step) => ({
        id: step.id,
        name: step.name,
        description: step.description,
        options: step.options.map((option) => buildOptionForExport(option)),
        uiPlugins: step.uiPlugins,
        triggers: step.triggers,
      })),
    };
    // Remove all null values from the final export
    return JSON.stringify(removeNullValues(orderedProcess), null, 2);
  };

  return {
    fetchProcesses,
    fetchProcess,
    deleteProcess,
    createProcess,
    updateProcess,
    deleteProcesses,
    createNewProcess,
    handleProcessInitSubmit,
    extractProcessNameFromProcdefUrl,
    extractProcessStepIdFromProcdefUrl,
    extractProcessStartStepIdFromProcessID,
    isUrl,
    handleProcessInitModalClose,
    cleanProcessBeforeSave,
    downloadProcess,
    exportProcessAsJson,
    exportProcessAsOrderedJson,
  };
}
